import { ShowingProgramFilters } from '../resources/showing/showing-filters';

type GeoLocation = { latitude: number; longitude: number; distance: number } | null;

export type ShowingsListKeyParams = {
    cinemaIds: number[];
    location: GeoLocation;
    selectedDate: string;
    filters: ShowingProgramFilters;
};

export type ShowingsByContentKeyParams = {
    cinemaIds: number[];
    location: GeoLocation;
    selectedDate: string;
};

export const showingKeys = {
    all: ['showings'] as const,
    lists: () => [...showingKeys.all, 'list'] as const,
    list: (params: ShowingsListKeyParams) => [...showingKeys.lists(), params] as const,
    byContent: (contentId: number | string, params: ShowingsByContentKeyParams) =>
        [...showingKeys.all, 'by-content', contentId, params] as const,
    detail: (id: number | string) => [...showingKeys.all, 'detail', id] as const,
    tags: (showingId: number | string) => [...showingKeys.all, 'tags', showingId] as const,
    /**
     * "How many showings match?" probes — the consumer surfaces that render a count or a peek sheet
     * rather than a list. `filters` must carry every parameter that narrows the request (content or
     * movie, cinemas, date window, location, active filter set), because the answer is a single
     * number and a collision is invisible until someone notices the wrong figure.
     */
    counts: (scope: string, filters: QueryKeyFilters = {}) => [...showingKeys.all, 'count', scope, filters] as const,
    /**
     * Paginated showing lists on the consumer surfaces (program detail, ticketing modal, the
     * favorite-cinemas finder). `scope` names the surface; `filters` must carry the content or movie
     * id along with everything else that narrows the request.
     */
    listBy: (scope: string, filters: QueryKeyFilters = {}) => [...showingKeys.lists(), scope, filters] as const
};

export const contentKeys = {
    all: ['contents'] as const,
    lists: () => [...contentKeys.all, 'list'] as const,
    list: (filters: QueryKeyFilters) => [...contentKeys.lists(), filters] as const,
    detail: (id: number | string) => [...contentKeys.all, 'detail', id] as const
};

export const cinemaKeys = {
    all: ['cinemas'] as const,
    favorites: (userId: number | string) => [...cinemaKeys.all, 'favorites', userId] as const,
    lists: () => [...cinemaKeys.all, 'list'] as const,
    list: (filters: QueryKeyFilters = {}) => [...cinemaKeys.lists(), filters] as const,
    /**
     * Proximity search. `latitude`/`longitude`/`distance` belong in `filters` — they change the
     * result set, so they must be part of the key.
     */
    nearby: (filters: QueryKeyFilters = {}) => [...cinemaKeys.all, 'nearby', filters] as const,
    /**
     * A specific set of cinemas looked up by id. `/cinemas` has no bulk `ids` filter, so callers
     * fan out over `getCinemaById` and hold the result as one row — hence a key over the whole set
     * rather than per cinema. Joined to a string so a caller passing a freshly built array (a
     * `useSearchParams` re-parse, say) still lands on the same entry.
     */
    byIds: (cinemaIds: ReadonlyArray<number | string>) => [...cinemaKeys.all, 'by-ids', cinemaIds.join(',')] as const,
    details: () => [...cinemaKeys.all, 'detail'] as const,
    detail: (cinemaId: number | string) => [...cinemaKeys.details(), cinemaId] as const,
    rooms: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...cinemaKeys.detail(cinemaId), 'rooms', filters] as const
};

/**
 * Serializable filter bag embedded into a list query key (page, perPage, search, sort, locale,
 * scalar ids, …). Keep it to plain scalars or scalar arrays: React Query hashes keys structurally,
 * so passing whole entities (a `cinema`/`user` object) makes keys unstable and invalidation
 * unreliable. Always pass `cinema.id` / `user.id`, never the object.
 */
export type QueryKeyFilters = Record<
    string,
    string | number | boolean | null | undefined | ReadonlyArray<string | number>
>;

/** Optional scalar params that further specialise a movie-v2 detail (locale, cinemaId, includes). */
export type MovieV2DetailParams = {
    locale?: string;
    cinemaId?: number | string;
    include?: string;
};

export const movieV2Keys = {
    all: ['movie-v2'] as const,
    lists: () => [...movieV2Keys.all, 'list'] as const,
    list: (filters: QueryKeyFilters) => [...movieV2Keys.lists(), filters] as const,
    details: () => [...movieV2Keys.all, 'detail'] as const,
    detail: (movieId: number | string, params: MovieV2DetailParams = {}) =>
        [...movieV2Keys.details(), movieId, params] as const
};

export const cinemaConfigurationKeys = {
    all: ['cinema-configuration'] as const,
    detail: (cinemaId: number | string, configurationId?: number | string) =>
        configurationId != null
            ? ([...cinemaConfigurationKeys.all, cinemaId, configurationId] as const)
            : ([...cinemaConfigurationKeys.all, cinemaId] as const)
};

export const eventRequestKeys = {
    all: ['event-requests'] as const,
    lists: () => [...eventRequestKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.lists(), cinemaId, filters] as const,
    details: () => [...eventRequestKeys.all, 'detail'] as const,
    detail: (eventRequestId: number | string) => [...eventRequestKeys.details(), eventRequestId] as const,
    count: (cinemaId?: number | string) =>
        cinemaId != null
            ? ([...eventRequestKeys.all, 'count', cinemaId] as const)
            : ([...eventRequestKeys.all, 'count'] as const),
    notes: (eventRequestId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.detail(eventRequestId), 'notes', filters] as const,
    dates: (eventRequestId: number | string) => [...eventRequestKeys.detail(eventRequestId), 'dates'] as const,
    /** Consumer-side list: the web app scopes event requests by user, not by cinema. */
    byUser: (userId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.lists(), 'by-user', userId, filters] as const,
    /**
     * As `byUser`, but for `useInfiniteQuery`/`usePagination` consumers. Deliberately a separate
     * key space: an infinite query stores `{ pages, pageParams }` where a plain query stores the
     * response itself, so the two cannot share a cache entry even when they issue the same request.
     */
    byUserInfinite: (userId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.lists(), 'by-user-infinite', userId, filters] as const,
    likes: (eventRequestId: number | string) => [...eventRequestKeys.detail(eventRequestId), 'likes'] as const,
    /** Comments the requester and the cinema exchange on one request. */
    comments: (eventRequestId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.detail(eventRequestId), 'comments', filters] as const,
    organizationComments: (eventRequestId: number | string, filters: QueryKeyFilters = {}) =>
        [...eventRequestKeys.detail(eventRequestId), 'organization-comments', filters] as const
};

export const cinemaProductKeys = {
    all: ['cinema-products'] as const,
    lists: () => [...cinemaProductKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...cinemaProductKeys.lists(), cinemaId, filters] as const,
    details: () => [...cinemaProductKeys.all, 'detail'] as const,
    detail: (productId: number | string) => [...cinemaProductKeys.details(), productId] as const,
    categories: (cinemaId: number | string) => [...cinemaProductKeys.all, 'categories', cinemaId] as const
};

export const dealKeys = {
    all: ['deals'] as const,
    lists: () => [...dealKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...dealKeys.lists(), cinemaId, filters] as const,
    details: () => [...dealKeys.all, 'detail'] as const,
    detail: (dealId: number | string) => [...dealKeys.details(), dealId] as const,
    /** Consumer-side list: the web deals page scopes by a selected set of cinemas, not one cinema. */
    byCinemas: (cinemaIds: ReadonlyArray<number | string>, filters: QueryKeyFilters = {}) =>
        [...dealKeys.lists(), 'by-cinemas', cinemaIds.join(','), filters] as const,
    products: (dealId: number | string, cinemaProductIds: ReadonlyArray<number | string>) =>
        [...dealKeys.detail(dealId), 'products', cinemaProductIds.join(',')] as const
};

export const flyerTemplateKeys = {
    all: ['flyer-templates'] as const,
    lists: () => [...flyerTemplateKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...flyerTemplateKeys.lists(), cinemaId, filters] as const,
    details: () => [...flyerTemplateKeys.all, 'detail'] as const,
    detail: (flyerTemplateId: number | string) => [...flyerTemplateKeys.details(), flyerTemplateId] as const
};

export const flyerKeys = {
    all: ['flyers'] as const,
    lists: () => [...flyerKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...flyerKeys.lists(), cinemaId, filters] as const,
    details: () => [...flyerKeys.all, 'detail'] as const,
    detail: (flyerId: number | string) => [...flyerKeys.details(), flyerId] as const,
    versions: (flyerId: number | string) => [...flyerKeys.detail(flyerId), 'versions'] as const
};

export const socialMediaTemplateKeys = {
    all: ['social-media-templates'] as const,
    lists: () => [...socialMediaTemplateKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...socialMediaTemplateKeys.lists(), cinemaId, filters] as const,
    details: () => [...socialMediaTemplateKeys.all, 'detail'] as const,
    detail: (socialMediaTemplateId: number | string) =>
        [...socialMediaTemplateKeys.details(), socialMediaTemplateId] as const
};

export const newsletterKeys = {
    campaigns: {
        all: (cinemaId: number | string) => ['newsletter-campaigns', cinemaId] as const,
        list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
            ['newsletter-campaigns', cinemaId, filters] as const,
        detail: (cinemaId: number | string, campaignId: number | string) =>
            ['newsletter-campaign-detail', cinemaId, campaignId] as const,
        stats: (cinemaId: number | string) => ['newsletter-campaigns-stats', cinemaId] as const
    },
    config: (cinemaId: number | string) => ['newsletter-config', cinemaId] as const,
    sections: (cinemaId: number | string) => ['newsletter-sections', cinemaId] as const,
    subscriptionStats: (cinemaId: number | string) => ['newsletter-subscription-stats', cinemaId] as const,
    subscribers: {
        all: (cinemaId: number | string) => ['newsletter-subscribers', cinemaId] as const,
        list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
            ['newsletter-subscribers', cinemaId, filters] as const,
        detail: (cinemaId: number | string, subscriberId: number | string) =>
            ['newsletter-subscriber-detail', cinemaId, subscriberId] as const,
        subscriptions: (cinemaId: number | string, subscriberId: number | string) =>
            ['newsletter-subscriber-subscriptions', cinemaId, subscriberId] as const
    }
};

/* ------------------------------------------------------------------------------------------------
 * Consumer-facing (web) domains
 *
 * The web app's cache is far longer-lived than the operator apps' (24h `gcTime`, `refetchOnMount:
 * false`, plus a 30-minute localStorage persister — see `apps/web/src/utils/Providers.tsx`), so a
 * key that omits a request parameter does not merely serve one stale render: it pins the wrong
 * answer for the rest of the session and across reloads. Every parameter the `queryFn` sends must
 * appear in the key here.
 * ---------------------------------------------------------------------------------------------- */

export const userKeys = {
    all: ['users'] as const,
    /** `GET /users/me`. Keyed by id so a logout/login swap cannot reuse the previous user's row. */
    me: (userId?: number | string) => [...userKeys.all, 'me', userId ?? null] as const,
    details: () => [...userKeys.all, 'detail'] as const,
    detail: (userId: number | string) => [...userKeys.details(), userId] as const
};

export const notificationPreferenceKeys = {
    all: ['notification-preferences'] as const,
    detail: (userId?: number | string) => [...notificationPreferenceKeys.all, userId ?? null] as const
};

export const messageKeys = {
    all: ['messages'] as const,
    lists: () => [...messageKeys.all, 'list'] as const,
    list: (userId: number | string | undefined, filters: QueryKeyFilters = {}) =>
        [...messageKeys.lists(), userId ?? null, filters] as const
};

export const userMovieListKeys = {
    all: ['user-movie-lists'] as const,
    lists: () => [...userMovieListKeys.all, 'list'] as const,
    list: (userId: number | string | undefined, filters: QueryKeyFilters = {}) =>
        [...userMovieListKeys.lists(), userId ?? null, filters] as const,
    /** Which of a user's lists already contain a given movie — drives the "add to list" toggle. */
    byMovie: (userId: number | string | undefined, movieId: number | string | undefined) =>
        [...userMovieListKeys.lists(), userId ?? null, 'by-movie', movieId ?? null] as const,
    details: () => [...userMovieListKeys.all, 'detail'] as const,
    detail: (userMovieListId: number | string) => [...userMovieListKeys.details(), userMovieListId] as const,
    items: (userMovieListId: number | string, filters: QueryKeyFilters = {}) =>
        [...userMovieListKeys.detail(userMovieListId), 'items', filters] as const
};

export const movieRatingKeys = {
    all: ['movie-ratings'] as const,
    lists: () => [...movieRatingKeys.all, 'list'] as const,
    /**
     * Prefix covering every filtered list for one movie — pass this to `invalidateQueries` after a
     * create/update/delete so the filters bag does not have to be reproduced at the call site.
     */
    byMovieAll: (movieId: number | string | undefined) => [...movieRatingKeys.lists(), 'by-movie', movieId ?? null],
    /** Public ratings for one movie. */
    byMovie: (movieId: number | string | undefined, filters: QueryKeyFilters = {}) =>
        [...movieRatingKeys.byMovieAll(movieId), filters] as const,
    /** The signed-in user's own rating of one movie (`/movie-ratings/me`). */
    mine: (movieId: number | string | undefined) => [...movieRatingKeys.all, 'mine', movieId ?? null] as const,
    /** The homepage's cross-movie "latest ratings" reel. */
    latest: (filters: QueryKeyFilters = {}) => [...movieRatingKeys.lists(), 'latest', filters] as const
};

export const movieTrailerKeys = {
    all: ['movie-trailers'] as const,
    lists: () => [...movieTrailerKeys.all, 'list'] as const,
    /** KinoCheck trailers for one movie. */
    byMovie: (movieId: number | string | undefined) =>
        [...movieTrailerKeys.lists(), 'by-movie', movieId ?? null] as const,
    latest: (filters: QueryKeyFilters = {}) => [...movieTrailerKeys.lists(), 'latest', filters] as const
};

export const searchKeys = {
    all: ['search'] as const,
    /**
     * Unified search. `filters` MUST carry `locale`: movie titles, posters and genre names are
     * resolved server-side per request locale, so two locales are two different payloads.
     */
    unified: (query: string, filters: QueryKeyFilters = {}) => [...searchKeys.all, 'unified', query, filters] as const
};

export const cityKeys = {
    all: ['cities'] as const,
    details: () => [...cityKeys.all, 'detail'] as const,
    detail: (cityId: number | string) => [...cityKeys.details(), cityId] as const,
    cinemas: (cityId: number | string, filters: QueryKeyFilters = {}) =>
        [...cityKeys.detail(cityId), 'cinemas', filters] as const,
    /** Fallback list for a city with no cinema of its own — a locale-scoped unified search. */
    nearbyCinemas: (cityId: number | string, filters: QueryKeyFilters = {}) =>
        [...cityKeys.detail(cityId), 'nearby-cinemas', filters] as const,
    /** City autocomplete (location picker). `filters` carries the query and the country scope. */
    search: (filters: QueryKeyFilters = {}) => [...cityKeys.all, 'search', filters] as const
};

/** Showtime program windows ("today", "this week", …) scoped to a city or a single cinema. */
export const programKeys = {
    all: ['program'] as const,
    byCity: (cityId: number | string | null | undefined, windowKey: string) =>
        [...programKeys.all, 'by-city', cityId ?? null, windowKey] as const,
    byCinema: (cinemaId: number | string, windowKey: string) =>
        [...programKeys.all, 'by-cinema', cinemaId, windowKey] as const
};

export const movieDateKeys = {
    all: ['movie-dates'] as const,
    details: () => [...movieDateKeys.all, 'detail'] as const,
    detail: (movieDateId: number | string) => [...movieDateKeys.details(), movieDateId] as const,
    options: (movieDateId: number | string, userId?: number | string) =>
        [...movieDateKeys.detail(movieDateId), 'options', userId ?? null] as const,
    /** Total vote count across a poll. */
    voteCount: (movieDateId: number | string) => [...movieDateKeys.detail(movieDateId), 'vote-count'] as const,
    /** Votes cast for one option of a poll. */
    optionVotes: (movieDateOptionId: number | string) =>
        [...movieDateKeys.all, 'option', movieDateOptionId, 'votes'] as const,
    /** The signed-in user's votes within a poll. */
    userVotes: (movieDateId: number | string, userId?: number | string) =>
        [...movieDateKeys.detail(movieDateId), 'user-votes', userId ?? null] as const
};

export const distributorMovieListKeys = {
    all: ['distributor-movie-lists'] as const,
    lists: () => [...distributorMovieListKeys.all, 'list'] as const,
    list: (filters: QueryKeyFilters = {}) => [...distributorMovieListKeys.lists(), filters] as const,
    details: () => [...distributorMovieListKeys.all, 'detail'] as const,
    detail: (distributorMovieListId: number | string) =>
        [...distributorMovieListKeys.details(), distributorMovieListId] as const,
    items: (distributorMovieListId: number | string, filters: QueryKeyFilters = {}) =>
        [...distributorMovieListKeys.detail(distributorMovieListId), 'items', filters] as const,
    /** Items across every distributor list — the unscoped `/distributor-movie-list-items` feed. */
    allItems: (filters: QueryKeyFilters = {}) => [...distributorMovieListKeys.all, 'items', filters] as const
};

export const cinemaMovieRecommendationKeys = {
    all: ['cinema-movie-recommendations'] as const,
    list: (cinemaId: number | string | undefined, filters: QueryKeyFilters = {}) =>
        [...cinemaMovieRecommendationKeys.all, cinemaId ?? null, filters] as const
};

/**
 * Homepage sections. The root segments are matched by string in `apps/web/src/utils/Providers.tsx`
 * to decide what may be persisted to localStorage — always compare against `homeKeys.*[0]`, never
 * against a re-typed literal, or the two drift silently.
 */
export const homeKeys = {
    latestTrailers: (filters: QueryKeyFilters = {}) => ['home-latest-trailers-infinite', filters] as const,
    latestRatings: (filters: QueryKeyFilters = {}) => ['home-latest-ratings-infinite', filters] as const,
    comingSoon: (filters: QueryKeyFilters = {}) => ['home-coming-soon', filters] as const,
    hotRightNow: (filters: QueryKeyFilters = {}) => ['home-hot-right-now', filters] as const,
    communityFavourites: (filters: QueryKeyFilters = {}) => ['home-community-favourites', filters] as const
};

/** `GET /advertisements/display` — keyed by visitor so the sticky A/B variant is per person. */
export const advertisementKeys = {
    all: ['advertisement'] as const,
    display: (filters: QueryKeyFilters = {}) => [...advertisementKeys.all, 'display', filters] as const,
    ipAddress: () => ['ip-address'] as const
};

export const cinemaCustomPageKeys = {
    all: ['cinema-custom-pages'] as const,
    lists: () => [...cinemaCustomPageKeys.all, 'list'] as const,
    list: (cinemaId: number | string, filters: QueryKeyFilters = {}) =>
        [...cinemaCustomPageKeys.lists(), cinemaId, filters] as const,
    details: () => [...cinemaCustomPageKeys.all, 'detail'] as const,
    detail: (cinemaCustomPageId: number | string) => [...cinemaCustomPageKeys.details(), cinemaCustomPageId] as const
};
